import { computed, effect, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, filter, pipe, Subject, switchMap, takeUntil, tap } from 'rxjs';

import { AddressesApi } from '../data/api/addresses.api';
import { errorCode as apiErrorCode } from '../data/api/api-error';
import type { Address, AddressDraft } from '../data/models/address/address.model';
import { toAddressDraft } from '../shared/utils/address-draft';
import { readStorage, writeStorage } from '../shared/utils/storage';
import { ADDRESS_FIELD_MAX, ADDRESS_MAX_ITEMS, STORAGE_KEYS } from './constants';

/** Код, а не текст: формулировку выбирает страница из словаря ngx-translate. */
export type AddressErrorCode = 'load' | 'save' | 'remove' | 'limit';

type AddressStatus = 'idle' | 'loading' | 'ready' | 'error';

type AddressState = {
  addresses: Address[];
  defaultAddressId: string | null;
  status: AddressStatus;
  /** Идёт создание или удаление: на это время кнопки списка и CTA заблокированы. */
  pending: boolean;
  errorCode: AddressErrorCode | null;
  created: Address | null;
};

/** В localStorage остаётся только выбор «по умолчанию»: у сервера такого поля нет, адреса — с бэкенда. */
function readStoredDefaultId(): string | null {
  const raw = readStorage(STORAGE_KEYS.ADDRESSES);
  // Значение недоверенное: пустая строка и длинный мусор идентификатором адреса быть не могут.
  return raw !== null && raw.length > 0 && raw.length <= ADDRESS_FIELD_MAX ? raw : null;
}

/** Дефолт всегда указывает на существующий адрес: сохранённый выбор, иначе первый в списке. */
function resolveDefaultId(
  addresses: readonly Address[],
  preferredId: string | null,
): string | null {
  if (preferredId !== null && addresses.some((address) => address.id === preferredId)) {
    return preferredId;
  }
  return addresses[0]?.id ?? null;
}

export const AddressStore = signalStore(
  { providedIn: 'root' },
  // Фабрика, а не готовый объект: хранилище читается при создании стора, а не при загрузке модуля.
  withState<AddressState>(() => ({
    addresses: [],
    defaultAddressId: readStoredDefaultId(),
    // Список не грузим при старте приложения: `load()` зовут страницы, которым он нужен.
    status: 'idle',
    pending: false,
    errorCode: null,
    created: null,
  })),

  withComputed(({ addresses, defaultAddressId, status, pending }) => ({
    defaultAddress: computed(
      () => addresses().find((address) => address.id === defaultAddressId()) ?? null,
    ),
    hasAddresses: computed(() => addresses().length > 0),
    isLoading: computed(() => status() === 'loading'),
    isLoadError: computed(() => status() === 'error'),
    isBusy: computed(() => pending()),
  })),

  withMethods((store, api = inject(AddressesApi)) => {
    // Логаут обрывает полёты: ответ, ушедший до него, вернул бы список уже чужому пользователю.
    const aborted = new Subject<void>();

    const applyList = (list: readonly Address[]): void => {
      const byId = new Map(list.map((item): [string, Address] => [item.id, item]));
      const addresses = [...byId.values()];
      patchState(store, {
        addresses,
        defaultAddressId: resolveDefaultId(addresses, store.defaultAddressId()),
        status: 'ready',
        errorCode: null,
      });
    };

    const applyCreated = (created: Address): void => {
      // Повтор id сломал бы @for track и радио «по умолчанию»: запись с тем же id убираем.
      const rest = store.addresses().filter((item) => item.id !== created.id);
      // Новый адрес первым: тем же порядком его отдаёт сервер, после перезагрузки список не прыгнет.
      const addresses = [created, ...rest];
      patchState(store, {
        addresses,
        defaultAddressId: resolveDefaultId(addresses, store.defaultAddressId()),
        created,
        pending: false,
      });
    };

    const load = rxMethod<void>(
      pipe(
        // Повторный вызов во время полёта не плодит запросы: список просят и «Мои адреса», и чекаут.
        filter(() => store.status() !== 'loading'),
        tap(() => patchState(store, { status: 'loading', errorCode: null })),
        switchMap(() =>
          api.list().pipe(
            tap((list) => applyList(list)),
            // catchError во ВНУТРЕННЕМ pipe: иначе поток умрёт и «Повторить» перестанет работать.
            catchError(() => {
              patchState(store, { status: 'error', errorCode: 'load' });
              return EMPTY;
            }),
            takeUntil(aborted),
          ),
        ),
      ),
    );

    const submit = rxMethod<AddressDraft>(
      pipe(
        // Повторный тап по «Сохранить» не должен создать второй адрес.
        filter(() => !store.pending()),
        // `created` не сбрасываем: чекаут отличает успех от отказа сравнением со снимком до запроса.
        tap(() => patchState(store, { pending: true, errorCode: null })),
        switchMap((draft) =>
          api.create(draft).pipe(
            tap((created) => applyCreated(created)),
            catchError((error: unknown) => {
              patchState(store, {
                pending: false,
                errorCode: apiErrorCode(error) === 'address_limit' ? 'limit' : 'save',
              });
              return EMPTY;
            }),
            takeUntil(aborted),
          ),
        ),
      ),
    );

    const removeOne = rxMethod<string>(
      pipe(
        filter((id) => !store.pending() && store.addresses().some((item) => item.id === id)),
        tap(() => patchState(store, { pending: true, errorCode: null })),
        switchMap((id) =>
          api.remove(id).pipe(
            tap(() => {
              const addresses = store.addresses().filter((item) => item.id !== id);
              patchState(store, {
                addresses,
                defaultAddressId: resolveDefaultId(addresses, store.defaultAddressId()),
                pending: false,
              });
            }),
            catchError(() => {
              patchState(store, { pending: false, errorCode: 'remove' });
              return EMPTY;
            }),
            takeUntil(aborted),
          ),
        ),
      ),
    );

    const submitDraft = (draft: AddressDraft): void => {
      // Лимит проверяем и на клиенте: заведомо обречённый POST вернёт 400 ADDRESS_LIMIT_EXCEEDED.
      if (store.addresses().length >= ADDRESS_MAX_ITEMS) {
        patchState(store, { errorCode: 'limit' });
        return;
      }
      const payload = toAddressDraft(draft);
      // Черновик без точки или улицы сервер отвергнет: форма такой не отдаёт, но молчать всё равно нельзя.
      if (payload === null) {
        patchState(store, { errorCode: 'save' });
        return;
      }
      submit(payload);
    };

    return {
      load,

      /** Для главной и профиля: список им нужен «как есть», бить в сеть на каждый вход незачем. */
      ensureLoaded(): void {
        if (store.status() === 'idle') {
          load();
        }
      },

      add(draft: AddressDraft): void {
        submitDraft(draft);
      },

      remove(id: string): void {
        removeOne(id);
      },

      setDefault(id: string): void {
        if (store.addresses().some((address) => address.id === id)) {
          patchState(store, { defaultAddressId: id });
        }
      },

      /** Обязателен на ЛЮБОЙ смене сессии, не только на логауте: иначе новый вход увидит чужие адреса. */
      reset(): void {
        aborted.next();
        patchState(store, {
          addresses: [],
          defaultAddressId: null,
          status: 'idle',
          pending: false,
          errorCode: null,
          created: null,
        });
      },
    };
  }),

  withHooks({
    onInit(store) {
      // Одностороннее зеркало: в localStorage живёт только выбранный адрес по умолчанию.
      effect(() => writeStorage(STORAGE_KEYS.ADDRESSES, store.defaultAddressId()));
    },
  }),
);
